import { Divider, Stack, DialogContent, DialogActions, Dialog, Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import { Strings } from '@app/constants';
import { FormProvider, RHFTextField } from '@app/components';
import { Header } from '@app/sections/header';
import { useRequestState } from '@app/hooks';
import { addDepartmentSchema } from '../validators';
import { IDepartment, IAddDepartmentSchema } from '../types';
import { addDepartment } from '../slice';
import { selectAddDepartmentState } from '../selectors';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IDepartment;
}

const AddDepartment = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();

  const methods = useForm<IAddDepartmentSchema>({
    resolver: yupResolver(addDepartmentSchema) as any,
    defaultValues: {
      name: get(row, 'name', ''),
      desc: get(row, 'desc', ''),
    },
  });

  const onSubmit = (form: IAddDepartmentSchema) => {
    const id = row?.id ?? 0;
    dispatch(addDepartment({ ...form, id }));
  };
  const { loading: saveDepartment } = useRequestState({
    stateSelector: selectAddDepartmentState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open} fullWidth>
          <Header
            variant="dialog"
            title={row ? Strings.department.updateDepartment : Strings.common.addDepartment}
            onClose={onClose}
          />
          <Divider />
          <DialogContent>
            <Stack spacing={2} my={2}>
              <RHFTextField
                name="name"
                required
                label={Strings.field.name}
                fullWidth
                variant="outlined"
                multiline
              />
              <RHFTextField
                name="desc"
                label={Strings.field.description}
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
              />
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={saveDepartment}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};

export { AddDepartment };
